import React, { useEffect, useRef, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import NavBar from "./components/NavBar";
import Footer from "./components/Footer";
import SheetView from "./views/SheetView";
import FieldView from "./views/FieldView";
import KmlView from "./views/KmlView";
import MapView from "./views/MapView";
import TasksView from "./views/TasksView";
import EmailView from "./views/EmailView";
import FormSettingsView from "./views/FormSettingsView";
import ImportView from "./views/ImportView";
import LanguageSwitcher from "./components/LanguageSwitcher";
import SetupWizard from "./components/SetupWizard";
import ProjectChooser from "./components/ProjectChooser";
import { isSignedIn, onAuthChange, signIn, signOut } from "./auth/googleAuth";
import {
  checkExistingProject,
  clearMewProjectData,
  ensureAllSheets,
  hasMewProjectData,
  isSpreadsheetOwnedByMe,
} from "./api/sheets";
import { useFieldworkerMode } from "./utils/fieldworkerMode";
import { resolveAndApplyMode } from "./utils/roleResolution";
import MembersView from "./views/MembersView";
import { useT } from "./i18n";

interface SetupBanner {
  url?: string;
  error?: string;
}

// ─── Startup loading screen ─────────────────────────────────────────────────

function StartupLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="relative">
        <img
          src={`${process.env.PUBLIC_URL || ""}/assets/logo_128x128.png`}
          srcSet={`${process.env.PUBLIC_URL || ""}/assets/logo_128x128.png 1x, ${process.env.PUBLIC_URL || ""}/assets/logo_256x256.png 2x`}
          alt="MeW"
          className="w-16 h-16 opacity-90"
        />
        <span className="absolute inset-0 -m-2.5 rounded-full border-4 border-pink-200 border-t-pink-600 animate-spin" />
      </div>
    </div>
  );
}

// ─── Stale data confirm modal ───────────────────────────────────────────────

function StaleDataModal({
  onClear,
  onKeep,
  t,
}: {
  onClear: () => void;
  onKeep: () => void;
  t: ReturnType<typeof useT>;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
        <div className="flex items-start gap-3">
          <span className="text-amber-500 text-xl mt-0.5">
            <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
          </span>
          <h2 className="text-lg font-semibold text-gray-900">{t("app.staleMewTitle")}</h2>
        </div>
        <p className="text-sm text-gray-600">{t("app.staleMewBody")}</p>
        <div className="rounded-lg bg-amber-50 border border-amber-100 p-3 space-y-1.5 text-sm text-amber-900">
          <p className="font-medium">{t("app.staleMewImplications")}</p>
          <ul className="list-disc pl-4 space-y-1">
            <li>{t("app.staleMewImp1")}</li>
            <li>{t("app.staleMewImp2")}</li>
            <li>{t("app.staleMewImp3")}</li>
          </ul>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 pt-1">
          <button
            type="button"
            onClick={onClear}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-pink-600 hover:bg-pink-700 text-white rounded-lg px-4 py-2.5 text-sm font-medium transition-colors"
          >
            <i className="fa-solid fa-broom" aria-hidden="true" />
            {t("app.staleMewClear")}
          </button>
          <button
            type="button"
            onClick={onKeep}
            className="flex-1 inline-flex items-center justify-center gap-2 border border-gray-300 hover:border-gray-400 text-gray-600 rounded-lg px-4 py-2.5 text-sm transition-colors"
          >
            {t("app.staleMewKeep")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── App ────────────────────────────────────────────────────────────────────

export default function App() {
  const [signedIn, setSignedIn] = useState(isSignedIn());
  const [authError, setAuthError] = useState("");
  const [setupBanner, setSetupBanner] = useState<SetupBanner | null>(null);
  // startupDone gates rendering — nothing mounts until we know which screen to show
  const [startupDone, setStartupDone] = useState(false);
  // onboarding screen to show when there is no usable project yet
  const [onboard, setOnboard] = useState<"chooser" | "wizard" | null>(null);
  const [staleModal, setStaleModal] = useState(false);
  const fieldworker = useFieldworkerMode();
  const t = useT();

  useEffect(() => onAuthChange(() => setSignedIn(isSignedIn())), []);

  // Reset startup state when user signs out so the next sign-in re-runs the check
  useEffect(() => {
    if (!signedIn) {
      startupRan.current = false;
      setStartupDone(false);
      setOnboard(null);
      setStaleModal(false);
    }
  }, [signedIn]);

  const startupRan = useRef(false);
  useEffect(() => {
    if (!signedIn || startupRan.current) return;
    startupRan.current = true;
    (async () => {
      try {
        const status = await checkExistingProject();

        if (status === "none") {
          setOnboard("chooser");
        } else if (status === "stale") {
          if (hasMewProjectData()) {
            setStaleModal(true);
          } else {
            setOnboard("chooser");
          }
        } else {
          // "valid" — existing project. Re-resolve the tier on every reload so a manager
          // promoting/demoting someone in the Members tab takes effect without re-joining.
          const sid = localStorage.getItem("mew_spreadsheet_id")!;
          const ownedByMe = await isSpreadsheetOwnedByMe(sid);
          const tier = await resolveAndApplyMode({ spreadsheetId: sid, ownedByMe });
          // Only managers may bootstrap/repair structure (this also adds the Members tab to
          // pre-existing projects); fieldworkers must never mutate the shared spreadsheet.
          if (tier === "manager") {
            await ensureAllSheets(sid);
          }
        }
      } catch (e) {
        console.warn("Startup check failed:", e);
      } finally {
        // Always mark done so we stop blocking the render
        setStartupDone(true);
      }
    })();
  }, [signedIn]);

  async function handleStaleModalClear() {
    clearMewProjectData();
    setStaleModal(false);
    setOnboard("chooser");
  }

  function handleStaleModalKeep() {
    setStaleModal(false);
    setOnboard("chooser");
  }

  async function handleWizardComplete(banner?: SetupBanner) {
    setOnboard(null);
    if (banner) setSetupBanner(banner);
  }

  async function handleSignIn() {
    setAuthError("");
    try {
      await signIn();
    } catch (e) {
      setAuthError((e as Error).message);
    }
  }

  if (!signedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-50 via-white to-accent-50 p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center space-y-4 border border-gray-100">
          <div className="flex justify-end">
            <LanguageSwitcher />
          </div>
          <img
            src={`${process.env.PUBLIC_URL || ""}/assets/logo_128x128.png`}
            srcSet={`${process.env.PUBLIC_URL || ""}/assets/logo_128x128.png 1x, ${process.env.PUBLIC_URL || ""}/assets/logo_256x256.png 2x, ${process.env.PUBLIC_URL || ""}/assets/logo_512x512.png 4x`}
            alt="MeW"
            className="mx-auto w-20 h-20"
          />
          <h1 className="text-2xl font-bold text-pink-700">{t("app.title")}</h1>
          <p className="text-sm text-gray-600">{t("app.signInPrompt")}</p>
          <button
            type="button"
            onClick={handleSignIn}
            className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-2 transition-colors"
          >
            <i className="fa-brands fa-google" aria-hidden="true" />
            <span>{t("app.signInGoogle")}</span>
          </button>
          {authError && <div className="text-sm text-red-600">{authError}</div>}
        </div>
      </div>
    );
  }

  // Signed in but startup check not yet resolved — show spinner, never mount routes
  if (!startupDone) {
    return <StartupLoader />;
  }

  if (staleModal) {
    return (
      <StaleDataModal
        onClear={handleStaleModalClear}
        onKeep={handleStaleModalKeep}
        t={t}
      />
    );
  }

  if (onboard === "chooser") {
    return (
      <ProjectChooser
        onCreate={() => setOnboard("wizard")}
        onJoined={() => setOnboard(null)}
      />
    );
  }

  if (onboard === "wizard") {
    return <SetupWizard onComplete={handleWizardComplete} />;
  }

  return (
    <div className="min-h-screen bg-white">
      {setupBanner && (
        <div className={`px-4 py-2 text-sm flex items-center gap-3 border-b ${setupBanner.error ? "bg-red-50 text-red-800 border-red-100" : "bg-amber-50 text-amber-900 border-amber-100"}`}>
          <i className={`fa-solid ${setupBanner.error ? "fa-circle-exclamation" : "fa-screwdriver-wrench"}`} aria-hidden="true" />
          <span className="flex-1">
            {setupBanner.error ? t("app.setupError", { msg: setupBanner.error }) : t("app.setupBanner")}
          </span>
          {setupBanner.url && (
            <button type="button" onClick={() => window.open(setupBanner.url, "_blank", "noopener")}
              className="inline-flex items-center gap-1.5 bg-amber-600 text-white rounded-md px-3 py-1 hover:bg-amber-700 transition-colors whitespace-nowrap">
              <i className="fa-solid fa-up-right-from-square" aria-hidden="true" />
              <span>{t("app.openForm")}</span>
            </button>
          )}
          <button type="button" onClick={() => setSetupBanner(null)} className="text-current opacity-60 hover:opacity-100" title={t("app.dismiss")} aria-label={t("app.dismiss")}>
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </div>
      )}
      <NavBar signedIn={signedIn} onSignIn={handleSignIn} onSignOut={signOut} />
      <Routes>
        <Route path="/" element={<FieldView />} />
        <Route path="/tasks" element={<TasksView />} />
        <Route path="/map" element={<MapView />} />
        {/* Fieldworkers (joined a shared project) only get Field entry + Tasks + Map. */}
        {!fieldworker && <Route path="/sheet" element={<SheetView />} />}
        {!fieldworker && <Route path="/kml" element={<KmlView />} />}
        {!fieldworker && <Route path="/email" element={<EmailView />} />}
        {!fieldworker && <Route path="/members" element={<MembersView />} />}
        {!fieldworker && <Route path="/form" element={<FormSettingsView />} />}
        {!fieldworker && <Route path="/import" element={<ImportView />} />}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Footer />
    </div>
  );
}
